import {
    DeleteOutlined,
    EditOutlined,
    PlusOutlined,
    SaveOutlined,
    CloseOutlined,
} from '@ant-design/icons';
import {
    Button,
    Card,
    Divider,
    Form,
    Input,
    InputNumber,
    message,
    Modal,
    Popconfirm,
    Select,
    Space,
    Table,
    Typography,
} from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApiRequest } from '../hooks/useApiRequest';
import useQuery from '../hooks/useQuery';

const { Title } = Typography;
const { Option } = Select;

interface PurityLevel {
    id: number;
    karatage: string;
    percent: number | null;
    combined: string | null;
    metalType: string | null;
    metalTypeId: number | null;
    referenceNumber: number | null;
    denumire: string | null;
}

interface PresetPrice {
    id: number;
    name: string;
    metalType: string;
    metalTypeId: number;
    purity: number;
    categoryId: number | null;
    loanPrice: number;
    extendedLoanPrice: number;
    salePrice: number | null;
    purchasePrice: number | null;
    createdAt: string;
    updatedAt: string | null;
}

interface ReferencePrice {
    id: number;
    metalId: number;
    metal: string | null;
    karatPercent: number;
    pret: number;
    pretDurataPrelungita: number;
}

interface MetalType {
    id: number;
    metalTypeId: number;
    denumire: string;
}

type NumericValue = number | string | null | undefined;

/**
 * Safely formats a numeric value with specified decimal places
 * Handles string values from database decimal columns
 */
const formatNumericValue = (value: NumericValue, decimals: number, suffix?: string): string => {
    if (value === null || value === undefined) return '-';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return '-';
    return suffix ? `${numValue.toFixed(decimals)}${suffix}` : numValue.toFixed(decimals);
};

const MetalPriceConfig = () => {
    const { t } = useTranslation();
    const { makeRequest } = useApiRequest();

    // Fetch purity levels
    const { data: purityLevelsData, loading: purityLoading } = useQuery<{
        success: boolean;
        data: PurityLevel[];
    }>({
        path: '/metal-price-config/purity-levels',
    });

    // Fetch preset prices
    const {
        data: presetPricesData,
        loading: presetLoading,
        reload: reloadPresetPrices,
    } = useQuery<{ success: boolean; data: PresetPrice[] }>({
        path: '/metal-price-config/preset-prices',
    });

    // Fetch reference prices
    const {
        data: referencePricesData,
        loading: referenceLoading,
        reload: reloadReferencePrices,
    } = useQuery<{ success: boolean; data: ReferencePrice[] }>({
        path: '/metal-price-config/reference-prices',
    });

    // Fetch metal types
    const { data: metalTypesData } = useQuery<{ success: boolean; data: MetalType[] }>({
        path: '/metal-price-config/metal-types',
    });

    const [presetModalVisible, setPresetModalVisible] = useState(false);
    const [referenceModalVisible, setReferenceModalVisible] = useState(false);
    const [editingPreset, setEditingPreset] = useState<PresetPrice | null>(null);
    const [editingReference, setEditingReference] = useState<ReferencePrice | null>(null);
    const [saving, setSaving] = useState(false);
    const [presetForm] = Form.useForm();
    const [referenceForm] = Form.useForm();

    const purityLevels = purityLevelsData?.data ?? [];
    const presetPrices = presetPricesData?.data ?? [];
    const referencePrices = referencePricesData?.data ?? [];
    const metalTypes = metalTypesData?.data ?? [];

    // Get unique metal type names from purity levels
    const uniqueMetalTypes = [...new Set(purityLevels.map(p => p.denumire).filter(Boolean))];

    // Get purity options for selected metal type
    const [selectedMetalType, setSelectedMetalType] = useState<string | null>(null);
    const purityOptions = purityLevels
        .filter(p => p.denumire === selectedMetalType)
        .map(p => p.percent)
        .filter((p): p is number => p !== null);

    useEffect(() => {
        if (editingPreset) {
            presetForm.setFieldsValue({
                name: editingPreset.name,
                metalType: editingPreset.metalType,
                purity: editingPreset.purity,
                categoryId: editingPreset.categoryId,
                loanPrice: editingPreset.loanPrice,
                extendedLoanPrice: editingPreset.extendedLoanPrice,
                salePrice: editingPreset.salePrice,
                purchasePrice: editingPreset.purchasePrice,
            });
            setSelectedMetalType(editingPreset.metalType);
        } else {
            presetForm.resetFields();
            setSelectedMetalType(null);
        }
    }, [editingPreset, presetForm]);

    useEffect(() => {
        if (editingReference) {
            referenceForm.setFieldsValue({
                metal: editingReference.metal,
                karatPercent: editingReference.karatPercent,
                pret: editingReference.pret,
                pretDurataPrelungita: editingReference.pretDurataPrelungita,
            });
        } else {
            referenceForm.resetFields();
        }
    }, [editingReference, referenceForm]);

    const handleSavePreset = useCallback(
        async (values: Record<string, unknown>) => {
            try {
                setSaving(true);
                const endpoint = editingPreset
                    ? `/metal-price-config/preset-prices/${editingPreset.id}`
                    : '/metal-price-config/preset-prices';
                const method = editingPreset ? 'PUT' : 'POST';

                await makeRequest({
                    path: endpoint,
                    method,
                    params: values,
                });

                message.success(
                    editingPreset
                        ? t('configuration.metalPrices.presetUpdated')
                        : t('configuration.metalPrices.presetCreated'),
                );
                setPresetModalVisible(false);
                setEditingPreset(null);
                reloadPresetPrices();
            } catch (error) {
                console.error('Error saving preset price:', error);
                message.error(t('configuration.metalPrices.saveError'));
            } finally {
                setSaving(false);
            }
        },
        [editingPreset, makeRequest, reloadPresetPrices, t],
    );

    const handleDeletePreset = useCallback(
        async (id: number) => {
            try {
                await makeRequest({
                    path: `/metal-price-config/preset-prices/${id}`,
                    method: 'DELETE',
                });
                message.success(t('configuration.metalPrices.presetDeleted'));
                reloadPresetPrices();
            } catch (error) {
                console.error('Error deleting preset price:', error);
                message.error(t('configuration.metalPrices.deleteError'));
            }
        },
        [makeRequest, reloadPresetPrices, t],
    );

    const handleSaveReference = useCallback(
        async (values: Record<string, unknown>) => {
            try {
                setSaving(true);
                const endpoint = editingReference
                    ? `/metal-price-config/reference-prices/${editingReference.id}`
                    : '/metal-price-config/reference-prices';
                const method = editingReference ? 'PUT' : 'POST';

                await makeRequest({
                    path: endpoint,
                    method,
                    params: values,
                });

                message.success(
                    editingReference
                        ? t('configuration.metalPrices.referenceUpdated')
                        : t('configuration.metalPrices.referenceCreated'),
                );
                setReferenceModalVisible(false);
                setEditingReference(null);
                reloadReferencePrices();
            } catch (error) {
                console.error('Error saving reference price:', error);
                message.error(t('configuration.metalPrices.saveError'));
            } finally {
                setSaving(false);
            }
        },
        [editingReference, makeRequest, reloadReferencePrices, t],
    );

    const handleDeleteReference = useCallback(
        async (id: number) => {
            try {
                await makeRequest({
                    path: `/metal-price-config/reference-prices/${id}`,
                    method: 'DELETE',
                });
                message.success(t('configuration.metalPrices.referenceDeleted'));
                reloadReferencePrices();
            } catch (error) {
                console.error('Error deleting reference price:', error);
                message.error(t('configuration.metalPrices.deleteError'));
            }
        },
        [makeRequest, reloadReferencePrices, t],
    );

    const presetColumns = [
        {
            title: t('configuration.metalPrices.name'),
            dataIndex: 'name',
            key: 'name',
            width: 150,
        },
        {
            title: t('configuration.metalPrices.metalType'),
            dataIndex: 'metalType',
            key: 'metalType',
            width: 100,
        },
        {
            title: t('configuration.metalPrices.purity'),
            dataIndex: 'purity',
            key: 'purity',
            width: 100,
            render: (value: NumericValue) => formatNumericValue(value, 1),
        },
        {
            title: t('configuration.metalPrices.category'),
            dataIndex: 'categoryId',
            key: 'categoryId',
            width: 80,
        },
        {
            title: t('configuration.metalPrices.loanPrice'),
            dataIndex: 'loanPrice',
            key: 'loanPrice',
            width: 120,
            render: (value: NumericValue) => formatNumericValue(value, 2),
        },
        {
            title: t('configuration.metalPrices.extendedLoanPrice'),
            dataIndex: 'extendedLoanPrice',
            key: 'extendedLoanPrice',
            width: 140,
            render: (value: NumericValue) => formatNumericValue(value, 2),
        },
        {
            title: t('configuration.metalPrices.salePrice'),
            dataIndex: 'salePrice',
            key: 'salePrice',
            width: 100,
            render: (value: NumericValue) => formatNumericValue(value, 2),
        },
        {
            title: t('configuration.metalPrices.purchasePrice'),
            dataIndex: 'purchasePrice',
            key: 'purchasePrice',
            width: 100,
            render: (value: NumericValue) => formatNumericValue(value, 2),
        },
        {
            title: t('configuration.metalPrices.actions'),
            key: 'actions',
            width: 100,
            render: (_: unknown, record: PresetPrice) => (
                <Space>
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => {
                            setEditingPreset(record);
                            setPresetModalVisible(true);
                        }}
                    />
                    <Popconfirm
                        title={t('configuration.metalPrices.confirmDelete')}
                        onConfirm={() => handleDeletePreset(record.id)}
                        okText={t('common.yes')}
                        cancelText={t('common.no')}
                    >
                        <Button type="link" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const referenceColumns = [
        {
            title: t('configuration.metalPrices.metalType'),
            dataIndex: 'metal',
            key: 'metal',
            width: 120,
        },
        {
            title: t('configuration.metalPrices.referenceKarat'),
            dataIndex: 'karatPercent',
            key: 'karatPercent',
            width: 150,
            render: (value: NumericValue) => formatNumericValue(value, 1, '%'),
        },
        {
            title: t('configuration.metalPrices.referencePrice'),
            dataIndex: 'pret',
            key: 'pret',
            width: 150,
            render: (value: NumericValue) => formatNumericValue(value, 2),
        },
        {
            title: t('configuration.metalPrices.extendedPrice'),
            dataIndex: 'pretDurataPrelungita',
            key: 'pretDurataPrelungita',
            width: 150,
            render: (value: NumericValue) => formatNumericValue(value, 2),
        },
        {
            title: t('configuration.metalPrices.actions'),
            key: 'actions',
            width: 100,
            render: (_: unknown, record: ReferencePrice) => (
                <Space>
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => {
                            setEditingReference(record);
                            setReferenceModalVisible(true);
                        }}
                    />
                    <Popconfirm
                        title={t('configuration.metalPrices.confirmDelete')}
                        onConfirm={() => handleDeleteReference(record.id)}
                        okText={t('common.yes')}
                        cancelText={t('common.no')}
                    >
                        <Button type="link" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const purityLevelColumns = [
        {
            title: t('configuration.metalPrices.karatage'),
            dataIndex: 'karatage',
            key: 'karatage',
            width: 100,
        },
        {
            title: t('configuration.metalPrices.percent'),
            dataIndex: 'percent',
            key: 'percent',
            width: 100,
            render: (value: NumericValue) => formatNumericValue(value, 1, '‰'),
        },
        {
            title: t('configuration.metalPrices.metalType'),
            dataIndex: 'denumire',
            key: 'denumire',
            width: 100,
        },
        {
            title: t('configuration.metalPrices.combined'),
            dataIndex: 'combined',
            key: 'combined',
            width: 120,
        },
        {
            title: t('configuration.metalPrices.referenceNumber'),
            dataIndex: 'referenceNumber',
            key: 'referenceNumber',
            width: 120,
            render: (value: NumericValue) => formatNumericValue(value, 3),
        },
    ];

    return (
        <div>
            {/* Preset Metal Prices Section */}
            <Card
                title={
                    <Space>
                        <Title level={5} style={{ margin: 0 }}>
                            {t('configuration.metalPrices.presetPricesTitle')}
                        </Title>
                    </Space>
                }
                extra={
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            setEditingPreset(null);
                            setPresetModalVisible(true);
                        }}
                    >
                        {t('configuration.metalPrices.addPreset')}
                    </Button>
                }
                style={{ marginBottom: 24 }}
            >
                <Typography.Paragraph type="secondary">
                    {t('configuration.metalPrices.presetPricesDescription')}
                </Typography.Paragraph>
                <Table
                    columns={presetColumns}
                    dataSource={presetPrices}
                    rowKey="id"
                    loading={presetLoading}
                    size="small"
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 1000 }}
                />
            </Card>

            {/* Reference Prices Section */}
            <Card
                title={
                    <Space>
                        <Title level={5} style={{ margin: 0 }}>
                            {t('configuration.metalPrices.referencePricesTitle')}
                        </Title>
                    </Space>
                }
                extra={
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            setEditingReference(null);
                            setReferenceModalVisible(true);
                        }}
                    >
                        {t('configuration.metalPrices.addReference')}
                    </Button>
                }
                style={{ marginBottom: 24 }}
            >
                <Typography.Paragraph type="secondary">
                    {t('configuration.metalPrices.referencePricesDescription')}
                </Typography.Paragraph>
                <Table
                    columns={referenceColumns}
                    dataSource={referencePrices}
                    rowKey="id"
                    loading={referenceLoading}
                    size="small"
                    pagination={{ pageSize: 10 }}
                />
            </Card>

            {/* Purity Levels Section (Read-only reference) */}
            <Card
                title={
                    <Title level={5} style={{ margin: 0 }}>
                        {t('configuration.metalPrices.purityLevelsTitle')}
                    </Title>
                }
            >
                <Typography.Paragraph type="secondary">
                    {t('configuration.metalPrices.purityLevelsDescription')}
                </Typography.Paragraph>
                <Table
                    columns={purityLevelColumns}
                    dataSource={purityLevels}
                    rowKey="id"
                    loading={purityLoading}
                    size="small"
                    pagination={{ pageSize: 15 }}
                />
            </Card>

            {/* Preset Price Modal */}
            <Modal
                title={
                    editingPreset
                        ? t('configuration.metalPrices.editPreset')
                        : t('configuration.metalPrices.addPreset')
                }
                open={presetModalVisible}
                onCancel={() => {
                    setPresetModalVisible(false);
                    setEditingPreset(null);
                }}
                footer={null}
                width={600}
            >
                <Form form={presetForm} layout="vertical" onFinish={handleSavePreset}>
                    <Form.Item
                        name="name"
                        label={t('configuration.metalPrices.name')}
                        rules={[
                            {
                                required: true,
                                message: t('configuration.metalPrices.nameRequired'),
                            },
                        ]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="metalType"
                        label={t('configuration.metalPrices.metalType')}
                        rules={[
                            {
                                required: true,
                                message: t('configuration.metalPrices.metalTypeRequired'),
                            },
                        ]}
                    >
                        <Select
                            placeholder={t('configuration.metalPrices.selectMetalType')}
                            onChange={(value: string) => setSelectedMetalType(value)}
                        >
                            {uniqueMetalTypes.map(mt => (
                                <Option key={mt} value={mt}>
                                    {mt}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="purity"
                        label={t('configuration.metalPrices.purity')}
                        rules={[
                            {
                                required: true,
                                message: t('configuration.metalPrices.purityRequired'),
                            },
                        ]}
                    >
                        <Select
                            placeholder={t('configuration.metalPrices.selectPurity')}
                            disabled={!selectedMetalType}
                        >
                            {purityOptions.map(p => (
                                <Option key={p} value={p}>
                                    {p.toFixed(1)}‰
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="categoryId" label={t('configuration.metalPrices.category')}>
                        <InputNumber style={{ width: '100%' }} min={1} max={10} />
                    </Form.Item>
                    <Divider />
                    <Form.Item
                        name="loanPrice"
                        label={t('configuration.metalPrices.loanPrice')}
                        rules={[
                            {
                                required: true,
                                message: t('configuration.metalPrices.loanPriceRequired'),
                            },
                        ]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            precision={2}
                            addonAfter="RON/g"
                        />
                    </Form.Item>
                    <Form.Item
                        name="extendedLoanPrice"
                        label={t('configuration.metalPrices.extendedLoanPrice')}
                        rules={[
                            {
                                required: true,
                                message: t('configuration.metalPrices.extendedLoanPriceRequired'),
                            },
                        ]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            precision={2}
                            addonAfter="RON/g"
                        />
                    </Form.Item>
                    <Form.Item name="salePrice" label={t('configuration.metalPrices.salePrice')}>
                        <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            precision={2}
                            addonAfter="RON/g"
                        />
                    </Form.Item>
                    <Form.Item
                        name="purchasePrice"
                        label={t('configuration.metalPrices.purchasePrice')}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            precision={2}
                            addonAfter="RON/g"
                        />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={saving}
                                icon={<SaveOutlined />}
                            >
                                {t('configuration.save')}
                            </Button>
                            <Button
                                onClick={() => {
                                    setPresetModalVisible(false);
                                    setEditingPreset(null);
                                }}
                                icon={<CloseOutlined />}
                            >
                                {t('common.cancel')}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Reference Price Modal */}
            <Modal
                title={
                    editingReference
                        ? t('configuration.metalPrices.editReference')
                        : t('configuration.metalPrices.addReference')
                }
                open={referenceModalVisible}
                onCancel={() => {
                    setReferenceModalVisible(false);
                    setEditingReference(null);
                }}
                footer={null}
                width={500}
            >
                <Form form={referenceForm} layout="vertical" onFinish={handleSaveReference}>
                    <Form.Item
                        name="metal"
                        label={t('configuration.metalPrices.metalType')}
                        rules={[
                            {
                                required: true,
                                message: t('configuration.metalPrices.metalTypeRequired'),
                            },
                        ]}
                    >
                        <Select placeholder={t('configuration.metalPrices.selectMetalType')}>
                            {metalTypes.map(mt => (
                                <Option key={mt.id} value={mt.denumire}>
                                    {mt.denumire}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="karatPercent"
                        label={t('configuration.metalPrices.referenceKarat')}
                        rules={[
                            {
                                required: true,
                                message: t('configuration.metalPrices.karatRequired'),
                            },
                        ]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            max={1000}
                            precision={1}
                            addonAfter="‰"
                        />
                    </Form.Item>
                    <Form.Item
                        name="pret"
                        label={t('configuration.metalPrices.referencePrice')}
                        rules={[
                            {
                                required: true,
                                message: t('configuration.metalPrices.priceRequired'),
                            },
                        ]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            precision={2}
                            addonAfter="RON/g"
                        />
                    </Form.Item>
                    <Form.Item
                        name="pretDurataPrelungita"
                        label={t('configuration.metalPrices.extendedPrice')}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            precision={2}
                            addonAfter="RON/g"
                        />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={saving}
                                icon={<SaveOutlined />}
                            >
                                {t('configuration.save')}
                            </Button>
                            <Button
                                onClick={() => {
                                    setReferenceModalVisible(false);
                                    setEditingReference(null);
                                }}
                                icon={<CloseOutlined />}
                            >
                                {t('common.cancel')}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default MetalPriceConfig;
